import { User } from '@src/entities/User/ui/components/User';
import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Table as TableComponent } from '@src/shared/ui/components/TableQuery';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { merge, orderBy } from 'lodash';
import React, { useMemo } from 'react';

import { DisabledBulkActions } from './components/DisabledBulkActions';
import configuration from './includes/configuration.json';
import { Styles } from './includes/styles';

/**
 * Table: AssociatedEids
 *
 * Displays a list of EID's associated with an action path within a DataTable.
 *
 * @param error
 * @param headerLeft
 * @param isLoading
 * @param requestArgs
 * @param requestFunction
 * @param requestKey
 * @param rows
 * @param setEids
 * @param setIsOpen
 * @param totalPages
 * @param totalRecords
 * @returns {Element}
 * @constructor
 */
export function Table({
  error = '',
  headerLeft = null,
  isLoading = false,
  requestArgs = {},
  requestFunction = () => {},
  requestKey = '',
  rows = [],
  setEids = () => {},
  setIsOpen = () => {},
  totalPages = 1,
  totalRecords = 0,
}) {
  const { hasPermission } = useCurrentUser();

  const canDetach = hasPermission('ActionPaths', 'detach');

  /**
   * renderCellActions
   *
   * Displays a dropdown menu with actions for the row.
   *
   * @param row
   * @returns {Element}
   */
  const renderCellActions = ({ row }) => {
    return (
      <User.Capability attributes={['detach']} resource="ActionPaths">
        <Button
          className="eid-remove-button"
          onClick={() => {
            setEids([row?.original?.eid]);
            setIsOpen(true);
          }}
          rounded
          size="$sm"
          variant="ghost"
        >
          <IconSymbol className="eid-remove-icon" icon="remove_circle_outline" variant="outlined" />
        </Button>
      </User.Capability>
    );
  };

  /**
   * renderCellEid
   *
   * Renders the EID in the cell with a link to the eid search results.
   *
   * @param cell
   * @returns {*}
   */
  const renderCellEid = ({ value }) => {
    return <Link href={`/analysis/eid-search/${value}`}>{value}</Link>;
  };

  /**
   * Columns for table.
   */
  const columns = useMemo(() => {
    return orderBy(
      configuration?.initialColumns.map((item) => {
        const column = item;

        if (column?.Header === 'EID') {
          column.Cell = renderCellEid;
        }

        if (column?.accessor === 'actions') {
          column.Cell = renderCellActions;
        }

        return column;
      })
    );
  }, [canDetach]);

  const bulkActions = [
    {
      icon: <IconSymbol className="eid-remove-icon" icon="remove_circle_outline" variant="outlined" />,
      isSeparated: false,
      label: 'Remove Association',
      onClick: ({ getSelectedRows }) => {
        const theEids = getSelectedRows().map((row) => {
          return row?.eid;
        });
        setEids([...theEids]);
        setIsOpen(true);
      },
    },
  ];

  const bulkActionsConfig = {
    bulkActions: canDetach ? bulkActions : [],
    showSelection: !canDetach
      ? () => {
          return false;
        }
      : true,
  };

  return (
    <ErrorHandler location="src/widgets/ActionPaths/AssociatedEids/components/Table/Table.jsx">
      <Styles id="associated-eids">
        <TableComponent
          {...{
            columns,
            configuration: merge({}, configuration, bulkActionsConfig),
            error,
            headerLeft: <DisabledBulkActions />,
            isLoading,
            noDataMessage: "There are no associated eid's.",
            requestArgs,
            requestFunction,
            requestKey,
            rows,
            totalPages,
            totalRecords,
          }}
        />
      </Styles>
    </ErrorHandler>
  );
}
